import React from 'react';
import { motion } from 'framer-motion';
import ScrollHeader from '../components/ScrollHeader';
import ImageModal from '../components/ImageModal';

const Chat: React.FC = () => {
  const [selectedImage, setSelectedImage] = React.useState<string | null>(null);

  return (
    <div className="max-w-4xl mx-auto">
      <ScrollHeader title="Chat with Ana" />
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="prose prose-lg dark:prose-invert max-w-none">
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
            Interact with Ana, your AI-powered DevOps assistant that generates infrastructure code from natural language.
          </p>

          <div className="bg-primary-500/10 dark:bg-primary-500/20 border-l-4 border-primary-500 p-6 rounded-r-lg mb-8">
            <h3 className="text-primary-600 dark:text-primary-400 mt-0 mb-2">💡 Key Concept</h3>
            <p className="mb-0">
              Ana Chat is your starting point for infrastructure creation. Simply describe what you need in natural language, 
              and Ana will generate the corresponding infrastructure code using industry best practices.
            </p>
          </div>

          <h2>Overview</h2>
          <p>
            The Chat interface is where you communicate with Ana to create, modify, and manage your infrastructure. 
            Ana understands natural language and can generate Terraform code, Kubernetes manifests, Docker configurations, 
            and more based on your requirements.
          </p>

          <div 
            className="my-8 rounded-lg overflow-hidden border-2 border-gray-200 dark:border-gray-700 cursor-pointer hover:border-primary-500 dark:hover:border-primary-400 transition-colors"
            onClick={() => setSelectedImage('/screenshots/chat/chat.png')}
          >
            <img 
              src="/screenshots/chat/chat.png" 
              alt="Ana Chat Interface" 
              className="w-full"
            />
          </div>

          <h2>How to Use Chat</h2>
          
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500 p-6 rounded-r-lg mb-6">
            <h4 className="text-yellow-800 dark:text-yellow-400 mt-0 mb-2">⚠️ Important Prerequisite</h4>
            <p className="mb-0 text-yellow-700 dark:text-yellow-300">
              You must create or select a project before using the chat feature. Navigate to the Projects page to get started.
            </p>
          </div>

          <h3>Describing Infrastructure Needs</h3>
          <p>
            Once you have a project selected, describe your infrastructure requirements in plain English. 
            Ana understands context and can handle complex requests.
          </p>

          <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg mb-6">
            <h4 className="text-gray-700 dark:text-gray-300 mt-0 mb-4">Example Prompts:</h4>
            <div className="space-y-4">
              <div className="bg-white dark:bg-gray-900 p-4 rounded-lg">
                <p className="font-semibold text-sm mb-2">Simple Request:</p>
                <p className="text-sm text-gray-600 dark:text-gray-400 italic mb-0">
                  "Create a Kubernetes cluster with 3 nodes on AWS"
                </p>
              </div>
              <div className="bg-white dark:bg-gray-900 p-4 rounded-lg">
                <p className="font-semibold text-sm mb-2">Detailed Request:</p>
                <p className="text-sm text-gray-600 dark:text-gray-400 italic mb-0">
                  "Set up a production-ready web application infrastructure on AWS in us-east-1 with: 
                  an Auto Scaling group of t3.medium EC2 instances behind an Application Load Balancer, 
                  a PostgreSQL RDS database with Multi-AZ deployment, an S3 bucket for static assets with CloudFront CDN, 
                  and proper security groups allowing only HTTPS traffic"
                </p>
              </div>
              <div className="bg-white dark:bg-gray-900 p-4 rounded-lg">
                <p className="font-semibold text-sm mb-2">Modification Request:</p>
                <p className="text-sm text-gray-600 dark:text-gray-400 italic mb-0">
                  "Add Redis ElastiCache for session storage and enable automated backups for the RDS database"
                </p>
              </div>
            </div>
          </div>

          <h3>What Ana Understands</h3>
          <p>
            Ana can interpret various types of infrastructure requests:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
            <div className="bg-light-surface dark:bg-dark-surface p-4 rounded-lg border border-light-border dark:border-dark-border">
              <h4 className="text-primary-500 dark:text-primary-400 mt-0 mb-2">Resource Types</h4>
              <ul className="text-sm space-y-1 mb-0">
                <li>• Compute (EC2, VMs, containers)</li>
                <li>• Databases (RDS, managed DBs)</li>
                <li>• Storage (S3, blob storage)</li>
                <li>• Networking (VPCs, load balancers)</li>
                <li>• Security (IAM, security groups)</li>
              </ul>
            </div>

            <div className="bg-light-surface dark:bg-dark-surface p-4 rounded-lg border border-light-border dark:border-dark-border">
              <h4 className="text-primary-500 dark:text-primary-400 mt-0 mb-2">Requirements</h4>
              <ul className="text-sm space-y-1 mb-0">
                <li>• Performance specs (CPU, memory)</li>
                <li>• Availability (Multi-AZ, regions)</li>
                <li>• Security (encryption, access)</li>
                <li>• Compliance (HIPAA, PCI-DSS)</li>
                <li>• Cost constraints (budget limits)</li>
              </ul>
            </div>

            <div className="bg-light-surface dark:bg-dark-surface p-4 rounded-lg border border-light-border dark:border-dark-border">
              <h4 className="text-primary-500 dark:text-primary-400 mt-0 mb-2">Architectures</h4>
              <ul className="text-sm space-y-1 mb-0">
                <li>• Microservices</li>
                <li>• Serverless</li>
                <li>• Monolithic</li>
                <li>• Event-driven</li>
                <li>• Data pipelines</li>
              </ul>
            </div>

            <div className="bg-light-surface dark:bg-dark-surface p-4 rounded-lg border border-light-border dark:border-dark-border">
              <h4 className="text-primary-500 dark:text-primary-400 mt-0 mb-2">Modifications</h4>
              <ul className="text-sm space-y-1 mb-0">
                <li>• Add new resources</li>
                <li>• Change configurations</li>
                <li>• Optimize costs</li>
                <li>• Improve security</li>
                <li>• Scale resources</li>
              </ul>
            </div>
          </div>

          <h2>What Ana Can Generate</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8">
            <div className="bg-light-surface dark:bg-dark-surface p-6 rounded-lg border border-light-border dark:border-dark-border">
              <h3 className="text-primary-500 dark:text-primary-400 mt-0">Infrastructure as Code</h3>
              <ul className="mb-0">
                <li><strong>Terraform:</strong> HCL configurations for any cloud</li>
                <li><strong>CloudFormation:</strong> AWS-native templates</li>
                <li><strong>ARM Templates:</strong> Azure Resource Manager</li>
                <li><strong>Pulumi:</strong> Multi-language IaC</li>
              </ul>
            </div>

            <div className="bg-light-surface dark:bg-dark-surface p-6 rounded-lg border border-light-border dark:border-dark-border">
              <h3 className="text-primary-500 dark:text-primary-400 mt-0">Container Orchestration</h3>
              <ul className="mb-0">
                <li><strong>Kubernetes:</strong> Deployments, services, ingress</li>
                <li><strong>Docker Compose:</strong> Multi-container apps</li>
                <li><strong>Helm Charts:</strong> Kubernetes packages</li>
                <li><strong>Kustomize:</strong> Configuration management</li>
              </ul>
            </div>

            <div className="bg-light-surface dark:bg-dark-surface p-6 rounded-lg border border-light-border dark:border-dark-border">
              <h3 className="text-primary-500 dark:text-primary-400 mt-0">CI/CD Pipelines</h3>
              <ul className="mb-0">
                <li><strong>GitHub Actions:</strong> Workflow YAML files</li>
                <li><strong>GitLab CI:</strong> Pipeline configurations</li>
                <li><strong>Jenkins:</strong> Jenkinsfile pipelines</li>
                <li><strong>Azure DevOps:</strong> Pipeline definitions</li>
              </ul>
            </div>

            <div className="bg-light-surface dark:bg-dark-surface p-6 rounded-lg border border-light-border dark:border-dark-border">
              <h3 className="text-primary-500 dark:text-primary-400 mt-0">Cloud Resources</h3>
              <ul className="mb-0">
                <li><strong>Compute:</strong> VMs, containers, serverless</li>
                <li><strong>Databases:</strong> SQL, NoSQL, caching</li>
                <li><strong>Networking:</strong> VPCs, load balancers, DNS</li>
                <li><strong>Security:</strong> IAM, secrets, encryption</li>
              </ul>
            </div>
          </div>

          <h2>Advanced Chat Features</h2>

          <h3>Context Awareness</h3>
          <p>
            Ana maintains context throughout the conversation:
          </p>
          <ul>
            <li>Remembers previous requests in the session</li>
            <li>Understands references to earlier components</li>
            <li>Builds upon existing infrastructure</li>
            <li>Suggests related improvements</li>
          </ul>

          <h3>Multi-Turn Conversations</h3>
          <p>
            Refine your infrastructure through iterative dialogue:
          </p>
          <ol>
            <li>
              <strong>Initial Request:</strong> "Create a web application on AWS"
            </li>
            <li>
              <strong>Ana Response:</strong> Generates basic web app infrastructure
            </li>
            <li>
              <strong>Follow-up:</strong> "Add a Redis cache and enable auto-scaling"
            </li>
            <li>
              <strong>Ana Response:</strong> Updates code with ElastiCache and Auto Scaling Group
            </li>
            <li>
              <strong>Refinement:</strong> "Make it highly available across 3 availability zones"
            </li>
            <li>
              <strong>Ana Response:</strong> Configures Multi-AZ deployment
            </li>
          </ol>

          <h3>Code Explanations</h3>
          <p>
            Ask Ana to explain any part of the generated code:
          </p>
          <ul>
            <li>"Why did you choose t3.medium instances?"</li>
            <li>"Explain the security group configuration"</li>
            <li>"What does this Terraform module do?"</li>
            <li>"How does the load balancer health check work?"</li>
          </ul>

          <h2>Best Practices for Effective Prompts</h2>

          <h3>Be Specific About Requirements</h3>
          <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg mb-6">
            <p className="font-semibold mb-3">Include these details when relevant:</p>
            <ul className="space-y-2 mb-0">
              <li><strong>Cloud Provider:</strong> AWS, Azure, or GCP</li>
              <li><strong>Region:</strong> us-east-1, eu-west-1, etc.</li>
              <li><strong>Environment:</strong> Development, staging, or production</li>
              <li><strong>Scale:</strong> Expected traffic, data volume</li>
              <li><strong>Budget:</strong> Cost constraints or limits</li>
              <li><strong>Compliance:</strong> HIPAA, SOC 2, PCI-DSS requirements</li>
              <li><strong>Performance:</strong> Latency, throughput needs</li>
            </ul>
          </div>

          <h3>Use Industry Terms</h3>
          <p>
            Ana understands DevOps and cloud terminology:
          </p>
          <ul>
            <li>High availability, fault tolerance, disaster recovery</li>
            <li>Blue-green deployment, canary releases</li>
            <li>Service mesh, API gateway, message queue</li>
            <li>Auto-scaling, load balancing, caching</li>
            <li>Encryption at rest, encryption in transit</li>
          </ul>

          <h3>Iterative Refinement</h3>
          <p>
            Start simple and add complexity:
          </p>
          <ol>
            <li>
              <strong>Start:</strong> "Create a basic web application infrastructure"
            </li>
            <li>
              <strong>Add Database:</strong> "Add a PostgreSQL database with automated backups"
            </li>
            <li>
              <strong>Add Caching:</strong> "Include Redis for session management"
            </li>
            <li>
              <strong>Add Monitoring:</strong> "Set up CloudWatch alarms for CPU and memory"
            </li>
            <li>
              <strong>Add Security:</strong> "Enable encryption and configure WAF"
            </li>
          </ol>

          <h2>Common Use Cases</h2>

          <div className="space-y-6 my-8">
            <div className="bg-light-surface dark:bg-dark-surface p-6 rounded-lg border border-light-border dark:border-dark-border">
              <h3 className="text-primary-500 dark:text-primary-400 mt-0 mb-3">🌐 Web Application Deployment</h3>
              <p className="mb-3"><strong>Prompt:</strong></p>
              <p className="text-sm italic bg-gray-100 dark:bg-gray-800 p-3 rounded mb-3">
                "Deploy a scalable web application on AWS with load balancing, auto-scaling, RDS database, 
                S3 for static files, CloudFront CDN, and Route53 for DNS"
              </p>
              <p className="text-sm"><strong>Ana Generates:</strong></p>
              <ul className="text-sm space-y-1 mb-0">
                <li>• VPC with public/private subnets across 3 AZs</li>
                <li>• Application Load Balancer with SSL/TLS</li>
                <li>• Auto Scaling Group with launch template</li>
                <li>• RDS PostgreSQL with Multi-AZ and automated backups</li>
                <li>• S3 bucket with CloudFront distribution</li>
                <li>• Route53 hosted zone and records</li>
                <li>• Security groups with least-privilege access</li>
              </ul>
            </div>

            <div className="bg-light-surface dark:bg-dark-surface p-6 rounded-lg border border-light-border dark:border-dark-border">
              <h3 className="text-primary-500 dark:text-primary-400 mt-0 mb-3">🔄 Microservices on Kubernetes</h3>
              <p className="mb-3"><strong>Prompt:</strong></p>
              <p className="text-sm italic bg-gray-100 dark:bg-gray-800 p-3 rounded mb-3">
                "Create a Kubernetes cluster on GKE with Istio service mesh, monitoring stack, 
                and CI/CD pipeline for microservices deployment"
              </p>
              <p className="text-sm"><strong>Ana Generates:</strong></p>
              <ul className="text-sm space-y-1 mb-0">
                <li>• GKE cluster with node pools</li>
                <li>• Istio installation and configuration</li>
                <li>• Prometheus and Grafana for monitoring</li>
                <li>• ArgoCD for GitOps deployments</li>
                <li>• Ingress controller with TLS</li>
                <li>• Network policies for pod security</li>
                <li>• Horizontal Pod Autoscaler configs</li>
              </ul>
            </div>

            <div className="bg-light-surface dark:bg-dark-surface p-6 rounded-lg border border-light-border dark:border-dark-border">
              <h3 className="text-primary-500 dark:text-primary-400 mt-0 mb-3">📊 Data Pipeline Infrastructure</h3>
              <p className="mb-3"><strong>Prompt:</strong></p>
              <p className="text-sm italic bg-gray-100 dark:bg-gray-800 p-3 rounded mb-3">
                "Build a data pipeline on AWS to ingest data from S3, process with Glue, 
                store in Redshift, and visualize with QuickSight"
              </p>
              <p className="text-sm"><strong>Ana Generates:</strong></p>
              <ul className="text-sm space-y-1 mb-0">
                <li>• S3 buckets for raw and processed data</li>
                <li>• AWS Glue crawlers and ETL jobs</li>
                <li>• Redshift cluster with proper sizing</li>
                <li>• IAM roles and policies</li>
                <li>• QuickSight setup and permissions</li>
                <li>• EventBridge rules for automation</li>
                <li>• CloudWatch logs and alarms</li>
              </ul>
            </div>

            <div className="bg-light-surface dark:bg-dark-surface p-6 rounded-lg border border-light-border dark:border-dark-border">
              <h3 className="text-primary-500 dark:text-primary-400 mt-0 mb-3">⚡ Serverless API</h3>
              <p className="mb-3"><strong>Prompt:</strong></p>
              <p className="text-sm italic bg-gray-100 dark:bg-gray-800 p-3 rounded mb-3">
                "Create a serverless REST API on AWS with Lambda functions, API Gateway, 
                DynamoDB for storage, and Cognito for authentication"
              </p>
              <p className="text-sm"><strong>Ana Generates:</strong></p>
              <ul className="text-sm space-y-1 mb-0">
                <li>• Lambda functions with proper IAM roles</li>
                <li>• API Gateway with REST endpoints</li>
                <li>• DynamoDB tables with GSIs</li>
                <li>• Cognito user pool and identity pool</li>
                <li>• CloudWatch logs and metrics</li>
                <li>• API Gateway usage plans and keys</li>
                <li>• CORS configuration</li>
              </ul>
            </div>
          </div>

          <h2>Tips for Better Results</h2>

          <h3>Provide Context</h3>
          <p>
            Help Ana understand your use case:
          </p>
          <ul>
            <li>Mention the application type (web app, API, data pipeline)</li>
            <li>Specify expected load (users, requests per second)</li>
            <li>Indicate data sensitivity (PII, financial data)</li>
            <li>State availability requirements (99.9%, 99.99%)</li>
          </ul>

          <h3>Ask Questions</h3>
          <p>
            Don't hesitate to ask Ana for clarification or alternatives:
          </p>
          <ul>
            <li>"What's the difference between t3.medium and t3.large for my use case?"</li>
            <li>"Should I use RDS or DynamoDB for this application?"</li>
            <li>"How can I reduce costs while maintaining performance?"</li>
            <li>"What security best practices should I follow?"</li>
          </ul>

          <h3>Request Explanations</h3>
          <p>
            Learn while you build:
          </p>
          <ul>
            <li>"Explain why you chose this architecture"</li>
            <li>"What are the trade-offs of this approach?"</li>
            <li>"How does this compare to alternative solutions?"</li>
            <li>"What are the security implications?"</li>
          </ul>

          <h2>Next Steps After Chat</h2>
          <p>
            Once Ana generates your infrastructure code:
          </p>
          <ol>
            <li>
              <strong>Review in Code Editor:</strong> Examine the complete generated code with syntax highlighting
            </li>
            <li>
              <strong>Visualize Architecture:</strong> Click the eye button to see an interactive diagram
            </li>
            <li>
              <strong>Make Adjustments:</strong> Ask Ana for changes or edit code manually
            </li>
            <li>
              <strong>Set Up Integration:</strong> Configure cloud provider credentials
            </li>
            <li>
              <strong>Preview Costs:</strong> Check estimated monthly costs before deploying
            </li>
            <li>
              <strong>Deploy:</strong> Use the Visualisation tool to deploy to your cloud
            </li>
          </ol>

          <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 p-6 rounded-r-lg mt-8">
            <h3 className="text-green-800 dark:text-green-400 mt-0 mb-2">✨ Pro Tips</h3>
            <ul className="text-green-700 dark:text-green-300 mb-0 space-y-2">
              <li>
                <strong>Start Broad, Then Refine:</strong> Begin with a high-level description, then add specific requirements
              </li>
              <li>
                <strong>Use Examples:</strong> Reference similar architectures you've seen or used
              </li>
              <li>
                <strong>Mention Constraints:</strong> Budget limits, compliance needs, or technical constraints upfront
              </li>
              <li>
                <strong>Ask for Alternatives:</strong> Request different approaches to compare options
              </li>
            </ul>
          </div>
        </div>
      </motion.div>

      <ImageModal
        isOpen={selectedImage !== null}
        src={selectedImage || ''}
        onClose={() => setSelectedImage(null)}
        alt="Screenshot"
      />
    </div>
  );
};

export default Chat;
